import { nafColor } from '@nafcore/theme';
import { colors } from '@naf-simba/theme';
import { Season } from '../../../../../../types/ElPrixContextData';
import { formatTestSeason } from '../../../../pages/Elbilguiden/RangeTests';
import { PointOfInterest } from './RouteCharts';

export type RangeData = { stated: number; measured: number };

export const createRouteChart = (
  altitudes: number[],
  pointsOfInterest: PointOfInterest[],
  season?: Season,
): Highcharts.SeriesOptionsType[] => [
  {
    name: 'Moh',
    data: altitudes,
    type: 'areaspline',
    lineColor: season === 'winter' ? nafColor.information.information : nafColor.information.successDark,
    color: season === 'winter' ? nafColor.information.informationLight : colors.secondary[50],
    zIndex: 0,
    marker: {
      symbol: 'circle',
      radius: 1,
      fillColor: nafColor.information.successDark,
      lineColor: nafColor.information.successDark,
    },
    accessibility: { keyboardNavigation: { enabled: false } },
  },
  {
    name: 'Steder langs ruten',
    data: pointsOfInterest
      .filter((point) => point.includeOnChart)
      .map((point) => ({ name: point.title, x: point.kmFromStart, y: point.altitude })),
    marker: {
      radius: 6,
      symbol: 'circle',
      fillColor: nafColor.signature.white,
      lineColor: season === 'winter' ? nafColor.information.information : nafColor.information.successDark,
      lineWidth: 2,
    },
    type: 'scatter',
    lineWidth: 0,
    states: { hover: { lineWidthPlus: 0 } },
    zIndex: 7,
  },
];

interface RangeChartOptions {
  altitudes: number[];
  rangeData: RangeData;
  repeatPoint: number;
  season?: Season;
  testDate?: string;
  pointsOfInterest: PointOfInterest[];
}

export const createRangeChart = ({
  altitudes,
  rangeData,
  repeatPoint,
  season,
  testDate,
  pointsOfInterest,
}: RangeChartOptions): Highcharts.SeriesOptionsType[] => {
  const { stated, measured } = rangeData;
  const statedAsInt = Math.round(stated);
  const measuredAsInt = Math.round(measured);
  const route = [...altitudes];
  const lowest = Math.min(statedAsInt, measuredAsInt);
  const largest = Math.max(statedAsInt, measuredAsInt);
  const loopLength = altitudes.length - repeatPoint;
  const measuredIsLower = measured < stated;
  const loopMultiplier = Math.ceil(Math.max(0, (Math.max(stated, measured) - altitudes.length) / loopLength));
  const loopSlice = altitudes.slice(repeatPoint);
  const seasonColor = season === 'winter' ? nafColor.information.information : nafColor.information.successDark;

  const getPoiColor = (x: number) => {
    if (x < lowest) return { lineColor: seasonColor, className: season };
    if (x <= largest) {
      return measuredIsLower
        ? { lineColor: nafColor.information.errorDark, className: 'shorter' }
        : { lineColor: nafColor.information.successDark, className: 'longer' };
    }
    return { lineColor: nafColor.neutral.neutral3, className: 'not-reached' };
  };

  for (let index = 0; index < loopMultiplier; index += 1) route.push(...loopSlice);

  const areaTypeConfig = {
    accessibility: { keyboardNavigation: { enabled: false } },
    zIndex: 0,
  };

  return [
    {
      ...areaTypeConfig,
      type: 'area',
      name: 'Distance to either wltp or measured',
      data: route.map((altitude, distance) => (distance <= lowest ? altitude : null)),
      lineColor: seasonColor,
      color: nafColor.neutral.neutral1,
      fillOpacity: 1,
    },
    {
      ...areaTypeConfig,
      type: 'area',
      name: 'Positive or negative difference',
      lineColor: measuredIsLower ? nafColor.information.errorDark : nafColor.information.successDark,
      color: measuredIsLower ? 'rgba(255, 204, 204, 1)' : 'rgba(198, 241, 211, 1)',
      fillOpacity: 1,
      data: route.map((altitude, distance) => (distance >= lowest && distance <= largest ? altitude : null)),
    },
    {
      ...areaTypeConfig,
      type: 'area',
      name: 'Rest of the route',
      lineColor: nafColor.neutral.neutral3,
      fillOpacity: 1,
      color: nafColor.neutral.neutral1,
      data: route.map((altitude, distance) => (distance >= largest ? altitude : null)),
    },
    {
      type: 'scatter',
      data: [{ name: formatTestSeason(season, testDate), x: measuredAsInt, y: route[measuredAsInt] }],
      marker: {
        radius: 6,
        symbol: 'diamond',
        fillColor: measuredIsLower ? nafColor.information.errorDark : nafColor.information.successDark,
      },
      zIndex: 7,
    },
    {
      type: 'scatter',
      data: [{ name: 'WLTP', x: statedAsInt, y: route[statedAsInt] }],
      marker: {
        radius: 1,
        symbol: 'square',
        fillColor: nafColor.signature.black,
        lineColor: nafColor.signature.black,
        lineWidth: 6,
      },
      zIndex: 7,
    },
    {
      name: 'Steder langs ruten',
      data: pointsOfInterest
        .filter((point) => point.includeOnChart)
        .map((point) => {
          const { className, lineColor } = getPoiColor(point.kmFromStart);
          return {
            name: point.title,
            x: point.kmFromStart,
            y: point.altitude,
            className,
            marker: {
              radius: 6,
              symbol: 'circle',
              fillColor: nafColor.signature.white,
              lineColor,
              lineWidth: 2,
            },
          };
        }),
      type: 'scatter',
      lineWidth: 0,
      states: { hover: { lineWidthPlus: 0 } },
      zIndex: 7,
    },
  ];
};
